import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/common/SEOHead';
import { LoadingPage } from '@/components/common/Loading';
import { Trophy, ChevronRight, Globe, Star, TrendingUp, Shield, Zap, MapPin, Calendar, Target, Crown, BarChart3 } from 'lucide-react';

const API_BASE = '';

const SECTION_CONFIG = {
  leagues: { icon: Trophy, title: 'Populer Ligler', color: 'blue', gradient: 'from-blue-950 to-indigo-950', border: 'border-blue-500/20' },
  matches: { icon: Calendar, title: 'Canli Oranli Maclar', color: 'emerald', gradient: 'from-emerald-950 to-teal-950', border: 'border-emerald-500/20' },
  bookmakers: { icon: Zap, title: 'Bahis Siteleri', color: 'amber', gradient: 'from-amber-950 to-yellow-950', border: 'border-amber-500/20' },
  cups: { icon: Crown, title: 'Kupalar ve Turnuvalar', color: 'violet', gradient: 'from-violet-950 to-purple-950', border: 'border-violet-500/20' },
  wc: { icon: Globe, title: 'Dunya Kupasi 2026', color: 'red', gradient: 'from-red-950 to-rose-950', border: 'border-red-500/20' },
  tools: { icon: Target, title: 'Araclar', color: 'cyan', gradient: 'from-cyan-950 to-sky-950', border: 'border-cyan-500/20' },
};

function AuthBadge({ score }) {
  const color = score >= 80 ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' : score >= 60 ? 'text-blue-400 bg-blue-500/10 border-blue-500/20' : 'text-slate-400 bg-slate-500/10 border-slate-600/20';
  return <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${color}`}>{score}</span>;
}

export default function SitemapHub() {
  const { currentLang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/api/sitemap-hub`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <LoadingPage />;
  if (!data) return null;

  const { urls, counts, total_indexable, total_excluded } = data;
  const L = currentLang;

  return (
    <div data-testid="sitemap-hub">
      <SEOHead title="Site Haritasi - Tum Sayfalar | ODDSIS" description="ODDSIS site haritasi. Tum futbol ligleri, maclar, bahis siteleri, kupalar ve Dunya Kupasi 2026 sayfalari." />

      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #020617 0%, #0a0a2e 40%, #1a0a3e 100%)' }}>
        <div className="relative z-10 container mx-auto px-4 max-w-7xl py-10">
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-4">
            <Link to={`/${L}`} className="hover:text-slate-200">Ana Sayfa</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-purple-400">Site Haritasi</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
            <MapPin className="w-8 h-8 inline mr-3 text-purple-400" />SITE HARITASI
          </h1>
          <p className="text-sm text-slate-400 mt-2">Tum ODDSIS sayfalarini kesfedin - {total_indexable} kaliteli sayfa</p>

          <div className="flex flex-wrap gap-3 mt-6">
            {Object.entries(counts).map(([key, count]) => {
              const cfg = SECTION_CONFIG[key];
              if (!cfg || count === 0) return null;
              return (
                <a key={key} href={`#${key}`} className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-xs font-semibold bg-${cfg.color}-500/10 text-${cfg.color}-400 border-${cfg.color}-500/20 hover:scale-105 transition-all`}>
                  <cfg.icon className="w-3.5 h-3.5" />
                  {cfg.title} ({count})
                </a>
              );
            })}
          </div>

          <div className="flex gap-6 mt-6 text-xs">
            <div className="text-emerald-400"><BarChart3 className="w-3 h-3 inline mr-1" />{total_indexable} Indexlenebilir</div>
            <div className="text-slate-500">{total_excluded} Haric Tutulan</div>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="container mx-auto px-4 max-w-7xl py-8 space-y-8">
        {Object.entries(SECTION_CONFIG).map(([key, cfg]) => {
          const items = urls?.[key] || [];
          if (items.length === 0) return null;
          const Icon = cfg.icon;
          return (
            <section key={key} id={key} data-testid={`sitemap-${key}`}>
              <div className={`rounded-2xl overflow-hidden border ${cfg.border}`}>
                <div className={`bg-gradient-to-r ${cfg.gradient} p-5`}>
                  <div className="flex items-center gap-3">
                    <Icon className="w-6 h-6 text-white/60" />
                    <h2 className="text-lg font-black text-white uppercase tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                      {cfg.title}
                    </h2>
                    <span className="text-xs bg-white/10 text-white/60 px-2.5 py-1 rounded-full">{items.length}</span>
                  </div>
                </div>
                <div className="p-4 bg-slate-950/50">
                  <div className={`grid gap-2 ${key === 'matches' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`}>
                    {items.map((item, i) => (
                      <SitemapCard key={i} item={item} lang={L} sectionKey={key} />
                    ))}
                  </div>
                </div>
              </div>
            </section>
          );
        })}

        {/* Static Pages */}
        <section id="static">
          <div className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-5">
            <h2 className="text-sm font-bold text-white uppercase mb-4">Diger Sayfalar</h2>
            <div className="flex flex-wrap gap-2">
              {[
                { url: '/best-odds', title: 'En Iyi Oranlar' },
                { url: '/football-predictions', title: 'Futbol Tahminleri' },
                { url: '/odds-comparison', title: 'Oran Karsilastirmasi' },
                { url: '/over-2-5-trends', title: 'Ust 2.5 Trendleri' },
                { url: '/btts-trends', title: 'KG Trendleri' },
                { url: '/hakkimizda', title: 'Hakkimizda' },
              ].map((p, i) => (
                <Link key={i} to={`/${L}${p.url}`} className="text-xs text-slate-400 hover:text-purple-300 bg-slate-800/30 hover:bg-purple-500/10 px-3 py-1.5 rounded-lg border border-transparent hover:border-purple-500/20 transition-all">
                  {p.title}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function SitemapCard({ item, lang, sectionKey }) {
  const url = `/${lang}${item.url}`;
  return (
    <Link to={url} className="group flex items-center gap-3 px-3 py-2.5 rounded-xl border border-slate-800/40 bg-slate-900/30 hover:bg-slate-800/30 hover:border-purple-500/20 transition-all" data-testid={`sitemap-link-${item.url}`}>
      {item.logo && (
        <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shrink-0">
          <img src={item.logo} alt="" className="w-6 h-6 object-contain" loading="lazy" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-slate-200 group-hover:text-purple-300 truncate">{item.title}</p>
        <div className="flex items-center gap-2 mt-0.5">
          {item.league && <span className="text-[9px] text-slate-500">{item.league}</span>}
          {item.country && <span className="text-[9px] text-slate-500">{item.country}</span>}
          {item.markets > 0 && <span className="text-[9px] text-emerald-500">{item.markets} market</span>}
          {item.matches > 0 && <span className="text-[9px] text-blue-400">{item.matches} mac</span>}
          {item.date && <span className="text-[9px] text-slate-600">{item.date}</span>}
        </div>
      </div>
      <AuthBadge score={item.authority || 50} />
      <ChevronRight className="w-3 h-3 text-slate-700 group-hover:text-purple-400 shrink-0" />
    </Link>
  );
}
